import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, FileText, Eye, Download, RefreshCcw, Trash2, Pencil, Check, X,
  CircleSlash, Plus, FolderOpen, CheckCircle2, Circle, Mail, Send, FolderDown,
} from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";

const fmtDDMMYYYY = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : s;
};

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ACCEPT = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";

// Convert a backend error into a short, friendly sentence a non-technical
// customer-service user can act on.
const friendlyOpenError = (err) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 401 || status === 403) {
    return "Your session has expired. Please sign in again and reopen the document.";
  }
  if (status === 404) return "This document is no longer on file.";
  if (status === 502 || status === 503 || status === 504) {
    return "The file store is not responding right now. Please try again in a moment.";
  }
  if (typeof detail === "string" && detail) return detail;
  if (!status) return "No connection to the server. Check your internet and try again.";
  return "This document could not be opened. Please try again.";
};

// Fetch a private document as a Blob (so the admin JWT can go in the
// Authorization header). On error, the caller handles the UX — no toast here.
const fetchDocBlob = async (customerId, doc) =>
  axios.get(
    `${API}/admin/customers/${customerId}/documents/${doc.id}/file`,
    { ...authHeaders(), responseType: "blob" }
  );

const openBlobInTab = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const CategoryHeader = ({ category, count, notNeeded, received, outstanding, onToggleReceived, onToggleNotNeeded, onDelete, canDelete, onRequest }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap">
    <div className="flex items-center gap-3 min-w-0">
      <button
        type="button"
        onClick={onToggleReceived}
        disabled={notNeeded}
        data-testid={`doc-received-toggle-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        title={
          notNeeded
            ? "Marked as not needed for this customer"
            : received
            ? "Tick to mark as not yet received"
            : "Tick when the client has sent this document"
        }
        className={`w-5 h-5 border flex items-center justify-center shrink-0 transition-colors ${
          notNeeded
            ? "border-forest/15 bg-cream-3 cursor-not-allowed"
            : received
            ? "bg-forest border-forest text-cream hover:bg-forest/90"
            : "border-forest/40 bg-white hover:border-forest"
        }`}
      >
        {received && !notNeeded && <Check size={13} strokeWidth={3} />}
        {notNeeded && <X size={11} className="text-forest/40" strokeWidth={3} />}
      </button>
      <h4 className={`font-serif text-[17px] truncate ${notNeeded ? "text-forest/40 line-through" : "text-forest"}`}>
        {category}
      </h4>
      {notNeeded ? (
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-forest/50 bg-cream-3 border border-forest/15 px-2 py-0.5">
          Not needed
        </span>
      ) : count > 0 ? (
        <span className="text-[11px] text-sage">
          {count} file{count === 1 ? "" : "s"}
        </span>
      ) : received ? (
        <span className="text-[11px] text-forest/70 font-semibold">Ticked as received</span>
      ) : (
        <span className="text-[11px] text-sage">Not received yet</span>
      )}
    </div>
    <div className="flex items-center gap-1">
      {outstanding && (
        <button
          type="button"
          onClick={onRequest}
          data-testid={`doc-request-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className="inline-flex items-center gap-1 text-[11px] font-bold tracking-[0.14em] uppercase text-gold-dark hover:text-forest hover:bg-gold transition-colors px-2 py-1.5 border border-gold/50 rounded-sm"
          title="Email the client about just this document"
        >
          <Mail size={12} /> Request
        </button>
      )}
      <button
        type="button"
        onClick={onToggleNotNeeded}
        className="text-[11px] font-bold tracking-[0.14em] uppercase text-forest/60 hover:text-forest transition-colors px-2 py-1.5"
        title={notNeeded ? "Mark as needed" : "Mark as not needed for this customer"}
      >
        {notNeeded ? "Mark Needed" : "Not Needed"}
      </button>
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="p-1.5 text-forest/50 hover:text-red-600 transition-colors"
          title="Remove custom category"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  </div>
);

const DocumentRow = ({ customerId, doc, onUpdated, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(doc.note || "");
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState(null); // { message, lastAction: 'view' | 'download' }
  const replaceRef = useRef(null);

  const handleOpen = async (download = false) => {
    setOpening(true);
    setOpenError(null);
    try {
      const resp = await fetchDocBlob(customerId, doc);
      if (download) triggerBlobDownload(resp.data, doc.filename);
      else openBlobInTab(resp.data, doc.filename);
    } catch (err) {
      setOpenError({ message: friendlyOpenError(err), lastAction: download ? "download" : "view" });
    } finally {
      setOpening(false);
    }
  };

  const setApplicant = async (val) => {
    try {
      await axios.put(
        `${API}/admin/customers/${customerId}/documents/${doc.id}`,
        { applicant: val },
        authHeaders()
      );
      onUpdated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };

  const saveNote = async () => {
    try {
      await axios.put(
        `${API}/admin/customers/${customerId}/documents/${doc.id}`,
        { note },
        authHeaders()
      );
      setEditing(false);
      onUpdated();
      toast.success("Note updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };

  const replace = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(
        `${API}/admin/customers/${customerId}/documents/${doc.id}/replace`,
        fd,
        authHeaders()
      );
      toast.success("File replaced");
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not replace");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div
      data-testid={`doc-row-${doc.id}`}
      className="bg-white border border-forest/10"
    >
    <div className="flex items-start justify-between gap-3 px-4 py-3">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <FileText size={18} strokeWidth={1.6} className="text-gold-dark shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-forest truncate" title={doc.filename}>{doc.filename}</p>
            {(() => {
              const cur = doc.applicant || "both";
              const next = cur === "both" ? "a1" : cur === "a1" ? "a2" : "both";
              const label = cur === "a1" ? "A1" : cur === "a2" ? "A2" : "Both";
              const style = cur === "both"
                ? "bg-cream-3 text-forest/60 border-forest/15"
                : "bg-amber-50 text-amber-800 border-amber-200";
              return (
                <button
                  type="button"
                  onClick={() => setApplicant(next)}
                  data-testid={`doc-applicant-${doc.id}`}
                  title={`Belongs to: ${label}. Click to change.`}
                  className={`inline-flex items-center text-[9.5px] font-bold tracking-[0.14em] uppercase ${style} px-1.5 py-0.5 rounded border hover:border-gold cursor-pointer`}
                >
                  {label}
                </button>
              );
            })()}
          </div>
          <p className="text-[11.5px] text-sage mt-0.5">
            {fmtDDMMYYYY(doc.uploaded_at)}
            {doc.size ? ` · ${fmtSize(doc.size)}` : ""}
          </p>
          {editing ? (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Payslips for March to May"
                maxLength={240}
                data-testid={`doc-note-input-${doc.id}`}
                className="flex-1 bg-cream-3 border border-forest/15 px-2.5 py-1.5 text-[12.5px] focus:border-gold focus:outline-none"
              />
              <button
                type="button"
                onClick={saveNote}
                className="p-1.5 text-forest hover:text-gold-dark"
                title="Save note"
              >
                <Check size={14} />
              </button>
              <button
                type="button"
                onClick={() => { setNote(doc.note || ""); setEditing(false); }}
                className="p-1.5 text-forest/50 hover:text-forest"
                title="Cancel"
              >
                <X size={14} />
              </button>
            </div>
          ) : doc.note ? (
            <p
              className="text-[12.5px] text-ink/80 mt-1 italic cursor-pointer hover:text-forest"
              onClick={() => setEditing(true)}
              title="Click to edit"
            >
              “{doc.note}”
            </p>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-[11.5px] text-forest/50 hover:text-forest mt-1"
            >
              + Add note
            </button>
          )}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <button type="button" onClick={() => handleOpen(false)} disabled={opening} data-testid={`doc-view-${doc.id}`} className="p-2 text-forest/70 hover:text-forest disabled:opacity-40" title="View"><Eye size={16} /></button>
        <button type="button" onClick={() => handleOpen(true)} disabled={opening} data-testid={`doc-download-${doc.id}`} className="p-2 text-forest/70 hover:text-forest disabled:opacity-40" title="Download"><Download size={16} /></button>
        <input ref={replaceRef} type="file" accept={ACCEPT} className="hidden" onChange={replace} />
        <button type="button" onClick={() => replaceRef.current.click()} className="p-2 text-forest/70 hover:text-forest" title="Replace"><RefreshCcw size={16} /></button>
        <button type="button" onClick={() => onDelete(doc)} className="p-2 text-forest/60 hover:text-red-600" title="Delete"><Trash2 size={16} /></button>
      </div>
    </div>
    {openError && (
      <div
        data-testid={`doc-open-error-${doc.id}`}
        className="flex flex-wrap items-center gap-3 border-t border-amber-200 bg-amber-50 px-4 py-2.5"
        role="alert"
      >
        <p className="text-[12.5px] text-amber-900 flex-1 min-w-[200px]">
          {openError.message}
        </p>
        <button
          type="button"
          onClick={() => handleOpen(openError.lastAction === "download")}
          disabled={opening}
          data-testid={`doc-open-retry-${doc.id}`}
          className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 px-3 py-1.5 rounded disabled:opacity-50"
        >
          <RefreshCcw size={12} strokeWidth={2} /> {opening ? "Retrying…" : "Retry"}
        </button>
        <button
          type="button"
          onClick={() => setOpenError(null)}
          className="p-1 text-amber-900/60 hover:text-amber-900"
          title="Dismiss"
        >
          <X size={13} />
        </button>
      </div>
    )}
    </div>
  );
};

const CategoryBlock = ({
  customerId, category, docs, notNeeded, received, canDelete,
  onUpload, onToggleReceived, onToggleNotNeeded, onDelete, onRefresh, onDeleteCustom, onRequest,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    for (const f of files) {
      await onUpload(category, f);
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  return (
    <section
      data-testid={`doc-cat-${category}`}
      className={`border p-4 sm:p-5 space-y-3 transition-colors ${notNeeded ? "bg-cream-3/50 border-forest/10" : "bg-cream-3 border-forest/15"}`}
    >
      <CategoryHeader
        category={category}
        count={docs.length}
        notNeeded={notNeeded}
        received={received}
        outstanding={!notNeeded && !received && docs.length === 0}
        onRequest={onRequest}
        onToggleReceived={onToggleReceived}
        onToggleNotNeeded={onToggleNotNeeded}
        canDelete={canDelete}
        onDelete={onDeleteCustom}
      />

      {!notNeeded && (
        <>
          {docs.length > 0 && (
            <div className="space-y-2">
              {docs.map((d) => (
                <DocumentRow
                  key={d.id}
                  customerId={customerId}
                  doc={d}
                  onUpdated={onRefresh}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current.click()}
            data-testid={`doc-drop-${category}`}
            className={`cursor-pointer border border-dashed px-4 py-5 text-center transition-colors ${
              dragOver
                ? "border-gold bg-gold/10"
                : "border-forest/25 hover:border-forest/50 bg-white/60"
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
            />
            <Upload size={18} strokeWidth={1.6} className="mx-auto text-forest/60 mb-1" />
            <p className="text-[12.5px] text-forest/70">
              <span className="font-semibold text-forest">Drop files</span> here or click to upload
            </p>
            <p className="text-[11px] text-sage mt-0.5">PDF, PNG or JPG · up to 25MB each</p>
          </div>
        </>
      )}
    </section>
  );
};


export const CustomerDocuments = ({ customerId }) => {
  const [data, setData] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [adding, setAdding] = useState(false);
  const [requestFor, setRequestFor] = useState(null); // category name
  const [requestNote, setRequestNote] = useState("");
  const [sendingRequest, setSendingRequest] = useState(false);
  const [zipOpen, setZipOpen] = useState(false);
  const [zipManifest, setZipManifest] = useState(null);
  const [zipCats, setZipCats] = useState(new Set());
  const [zipIncludeForms, setZipIncludeForms] = useState(true);
  const [zipping, setZipping] = useState(false);

  const load = useCallback(async () => {
    try {
      const resp = await axios.get(
        `${API}/admin/customers/${customerId}/documents`,
        authHeaders()
      );
      setData(resp.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load documents");
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (category, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    fd.append("note", "");
    try {
      await axios.post(`${API}/admin/customers/${customerId}/documents`, fd, authHeaders());
      await load();
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Could not upload ${file.name}`);
    }
  };

  const removeDoc = async (doc) => {
    if (!window.confirm(`Delete “${doc.filename}”? This cannot be undone.`)) return;
    try {
      await axios.delete(
        `${API}/admin/customers/${customerId}/documents/${doc.id}`,
        authHeaders()
      );
      await load();
      toast.success("Document deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete");
    }
  };

  const toggleNotNeeded = async (category, currentlyNotNeeded) => {
    try {
      await axios.patch(
        `${API}/admin/customers/${customerId}/categories/${encodeURIComponent(category)}`,
        { not_needed: !currentlyNotNeeded },
        authHeaders()
      );
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };

  const toggleReceived = async (category, currentlyReceived, hasUploads) => {
    // When a category already has at least one uploaded file, the tick stays
    // on by design — the admin would untick by removing the uploaded file.
    if (hasUploads) {
      toast(`"${category}" already has files uploaded.`);
      return;
    }
    try {
      await axios.patch(
        `${API}/admin/customers/${customerId}/categories/${encodeURIComponent(category)}`,
        { received: !currentlyReceived },
        authHeaders()
      );
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };

  const addCustomCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    try {
      await axios.post(
        `${API}/admin/customers/${customerId}/categories`,
        { name },
        authHeaders()
      );
      setNewCat("");
      setAdding(false);
      await load();
      toast.success(`Added “${name}”`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add");
    }
  };

  const removeCustomCategory = async (name) => {
    if (!window.confirm(`Remove the “${name}” category? All files inside will be deleted.`)) return;
    try {
      await axios.delete(
        `${API}/admin/customers/${customerId}/categories/${encodeURIComponent(name)}`,
        authHeaders()
      );
      await load();
      toast.success(`Removed “${name}”`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove");
    }
  };

  const sendItemRequest = async () => {
    if (!requestFor) return;
    setSendingRequest(true);
    try {
      await axios.post(
        `${API}/admin/customers/${customerId}/documents/categories/${encodeURIComponent(requestFor)}/request-email`,
        { note: requestNote },
        authHeaders(),
      );
      toast.success(`Request sent · ${requestFor}`);
      setRequestFor(null);
      setRequestNote("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send request");
    } finally {
      setSendingRequest(false);
    }
  };

  const customSet = useMemo(() => new Set(data?.custom_categories || []), [data]);

  if (!data) return <p className="text-sm text-sage">Loading documents…</p>;

  const { categories, documents, not_needed, received_flags, progress } = data;
  const notNeededSet = new Set(not_needed || []);
  const receivedFlags = received_flags || {};
  const openZip = async () => {
    try {
      const { data: m } = await axios.get(
        `${API}/admin/customers/${customerId}/documents/zip-manifest`,
        authHeaders(),
      );
      setZipManifest(m);
      setZipCats(new Set(
        (m.categories || []).filter((c) => c.included_by_default).map((c) => c.name)
      ));
      setZipIncludeForms(true);
      setZipOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open ZIP dialog");
    }
  };

  const runZip = async () => {
    if (!zipManifest) return;
    setZipping(true);
    const toastId = toast.loading("Preparing your ZIP…");
    try {
      const resp = await axios.post(
        `${API}/admin/customers/${customerId}/documents/download-zip`,
        { include_forms: zipIncludeForms, categories: Array.from(zipCats) },
        { ...authHeaders(), responseType: "blob" },
      );
      // Pull filename out of Content-Disposition (defensive — fall back to default).
      const cd = resp.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const fallbackName = `${zipManifest.customer_name || "Customer"} ${new Date()
        .toLocaleDateString("en-GB")
        .replaceAll("/", "-")}.zip`;
      const filename = match ? match[1] : fallbackName;
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`ZIP ready · ${filename}`, { id: toastId });
      setZipOpen(false);
    } catch (e) {
      // Axios may return a blob error body — try to parse it.
      let detail = "";
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ }
      }
      toast.error(detail || formatApiErrorDetail(e.response?.data?.detail) || "Could not download ZIP", { id: toastId });
    } finally {
      setZipping(false);
    }
  };

  const toggleZipCat = (name) => {
    setZipCats((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const percent = progress.applicable
    ? Math.round((progress.received / progress.applicable) * 100)
    : 0;

  return (
    <div className="space-y-5" data-testid="customer-documents">
      {/* Progress bar */}
      <div className="bg-white border border-forest/10 px-5 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <FolderOpen size={18} className="text-gold-dark" />
            <p className="text-[13px] text-forest">
              <span className="font-serif text-xl text-forest" data-testid="docs-progress">
                {progress.received} of {progress.applicable}
              </span>{" "}
              <span className="text-forest/70">received</span>
              {progress.applicable !== progress.total && (
                <span className="text-sage ml-2 text-[12px]">
                  ({progress.total - progress.applicable} marked not needed)
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={openZip}
              data-testid="docs-zip-open"
              title="Download every file for this customer as a single ZIP"
              className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase px-3 py-1.5 border border-forest/25 bg-white text-forest hover:border-gold hover:bg-gold/10 rounded-sm transition-colors"
            >
              <FolderDown size={13} /> Download all as ZIP
            </button>
            <p className="text-[13px] font-semibold text-forest">{percent}%</p>
          </div>
        </div>
        <div className="mt-3 h-2 bg-cream-3 rounded-full overflow-hidden">
          <div
            className="h-full bg-gold transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      <div className="space-y-4">
        {categories.map((cat) => (
          <CategoryBlock
            key={cat}
            customerId={customerId}
            category={cat}
            docs={documents[cat] || []}
            notNeeded={notNeededSet.has(cat)}
            received={!!receivedFlags[cat]}
            canDelete={customSet.has(cat)}
            onUpload={upload}
            onDelete={removeDoc}
            onToggleReceived={() => toggleReceived(cat, !!receivedFlags[cat], (documents[cat] || []).length > 0)}
            onToggleNotNeeded={() => toggleNotNeeded(cat, notNeededSet.has(cat))}
            onDeleteCustom={() => removeCustomCategory(cat)}
            onRefresh={load}
            onRequest={() => { setRequestFor(cat); setRequestNote(""); }}
          />
        ))}
      </div>

      {/* Add custom category */}
      {adding ? (
        <div className="bg-white border border-forest/15 p-4 flex items-center gap-2">
          <input
            type="text"
            autoFocus
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addCustomCategory(); }}
            placeholder="e.g. Marriage Certificate"
            maxLength={80}
            data-testid="docs-custom-cat-input"
            className="flex-1 bg-cream-3 border border-forest/15 px-3 py-2.5 text-sm focus:border-gold focus:outline-none"
          />
          <button type="button" onClick={addCustomCategory} data-testid="docs-custom-cat-save" className="btn-forest !py-2.5 text-[12px]">Add</button>
          <button type="button" onClick={() => { setAdding(false); setNewCat(""); }} className="btn-ghost-dark !py-2.5 text-[12px]">Cancel</button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          data-testid="docs-custom-cat-add"
          className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.14em] uppercase text-forest hover:text-gold-dark transition-colors"
        >
          <Plus size={14} /> Add Custom Category
        </button>
      )}

      {requestFor && (
        <div className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm flex items-center justify-center p-5">
          <div
            data-testid="doc-request-modal"
            className="bg-white border border-forest/15 rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.2)] max-w-md w-full p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] tracking-[0.24em] uppercase text-gold font-bold">Request this document</p>
                <h3 className="font-serif text-lg text-forest mt-1 truncate">{requestFor}</h3>
              </div>
              <button type="button" onClick={() => setRequestFor(null)} className="p-1 text-forest/60 hover:text-forest"><X size={18} /></button>
            </div>
            <p className="text-[13px] text-sage">
              We'll email the client about just this one item — not the full list.
            </p>
            <div>
              <label className="block text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60 mb-1.5">Add a note (optional)</label>
              <textarea
                rows={3}
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                maxLength={600}
                data-testid="doc-request-note"
                placeholder={`e.g. We've got everything else — just this ${requestFor.toLowerCase()} to tidy up your file.`}
                className="w-full bg-cream-3 border border-forest/15 rounded-lg px-3 py-2.5 text-sm focus:border-gold focus:outline-none"
              />
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <button type="button" onClick={() => setRequestFor(null)} className="btn-ghost-dark">Cancel</button>
              <button
                type="button"
                onClick={sendItemRequest}
                disabled={sendingRequest}
                data-testid="doc-request-send"
                className="btn-gold disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Send size={14} /> {sendingRequest ? "Sending…" : "Send request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {zipOpen && zipManifest && (
        <div className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm flex items-center justify-center p-5">
          <div
            data-testid="docs-zip-modal"
            className="bg-white border border-forest/15 rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.2)] max-w-xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] tracking-[0.24em] uppercase text-gold font-bold">Download as ZIP</p>
                <h3 className="font-serif text-lg text-forest mt-1 truncate">
                  {zipManifest.customer_name}
                </h3>
              </div>
              <button type="button" onClick={() => setZipOpen(false)} className="p-1 text-forest/60 hover:text-forest"><X size={18} /></button>
            </div>

            <p className="text-[12.5px] text-sage">
              Pick what to include. Each category becomes a folder inside the ZIP. Files stay private — only logged-in staff can download them.
            </p>

            {/* Signed applications toggle */}
            <div className="border border-forest/15 rounded-sm px-3 py-2.5 bg-cream-3">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={zipIncludeForms}
                  onChange={(e) => setZipIncludeForms(e.target.checked)}
                  data-testid="docs-zip-include-forms"
                  className="mt-0.5 accent-forest"
                />
                <span className="text-[13px] text-forest">
                  <strong>Include signed application forms</strong>{" "}
                  <span className="text-sage">
                    ({zipManifest.signed_forms.length} ready{zipManifest.unsigned_forms.length > 0 ? ` · ${zipManifest.unsigned_forms.length} unsigned will be skipped` : ""})
                  </span>
                </span>
              </label>
              {zipIncludeForms && zipManifest.unsigned_forms.length > 0 && (
                <ul className="mt-2 pl-7 text-[11.5px] text-sage space-y-0.5">
                  {zipManifest.unsigned_forms.map((f) => (
                    <li key={f.id} data-testid={`docs-zip-skipped-${f.id}`}>· {f.label} — not yet signed, skipped</li>
                  ))}
                </ul>
              )}
            </div>

            {/* Categories */}
            <div>
              <p className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60 mb-2">
                Categories to include
              </p>
              <ul className="divide-y divide-forest/10 border border-forest/15 rounded-sm bg-white max-h-72 overflow-y-auto">
                {zipManifest.categories.map((c) => {
                  const checked = zipCats.has(c.name);
                  const disabled = c.file_count === 0 || c.not_needed;
                  return (
                    <li key={c.name} className={`flex items-center justify-between gap-3 px-3 py-2 ${disabled ? "opacity-60" : ""}`}>
                      <label className="flex items-center gap-2.5 cursor-pointer flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={checked && !disabled}
                          disabled={disabled}
                          onChange={() => toggleZipCat(c.name)}
                          data-testid={`docs-zip-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="accent-forest"
                        />
                        <span className={`text-[13px] truncate ${disabled ? "text-forest/50 line-through" : "text-forest"}`}>
                          {c.name}
                        </span>
                      </label>
                      <span className={`text-[11px] shrink-0 ${c.file_count > 0 ? "text-forest/70 font-semibold" : "text-sage"}`}>
                        {c.not_needed ? "Not needed" : c.file_count === 0 ? "No files yet" : `${c.file_count} file${c.file_count === 1 ? "" : "s"}`}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[11px] text-sage mt-1.5">Categories marked "Not needed" or with no files are already left out.</p>
            </div>

            {zipCats.size === 0 && !zipIncludeForms && (
              <p className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm">
                Nothing selected. Tick at least one category or the application forms.
              </p>
            )}

            <div className="flex gap-2 justify-end pt-1">
              <button type="button" onClick={() => setZipOpen(false)} className="btn-ghost-dark">Cancel</button>
              <button
                type="button"
                onClick={runZip}
                disabled={zipping || (zipCats.size === 0 && !zipIncludeForms)}
                data-testid="docs-zip-download"
                className="btn-gold disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Download size={14} /> {zipping ? "Preparing…" : "Download ZIP"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
